/**
 * Driver runs on the phone (docs/mobile-design.md Phase 3, chunk 1, §4.13), shared by mobile web and React Native:
 * which run is "today's", the next stop, what blocks a proof of delivery (mirrors the server's checks), the failed
 * delivery reasons, the Navigate link (Apple Maps on Apple devices, Google Maps elsewhere) and the offline overlay:
 * stop actions waiting in the outbox (docs/offline.md) are shown as done straight away, marked "waiting to send".
 * Pure TS.
 */
import { DateTime } from 'luxon';
import type { Journey, JourneyStop, RunView } from '../fleet/types';
import { formatInstantGB } from '../gbDate';

// ------------------------------------------------------------------------------------------------ today's runs
type RunListItem = Pick<Journey, 'id' | 'ref' | 'status' | 'planned_start' | 'planned_end'>;
const londonDay = (iso: string | number | Date) =>
  formatInstantGB(typeof iso === 'string' ? new Date(iso) : iso, 'yyyy-MM-dd');

/** The my-runs window the phone asks for (from a week ago to a week ahead, UK dates; the API's `to` is exclusive). */
export function myRunsRange(now: number = Date.now()): [string, string] {
  const d = DateTime.fromMillis(now).setZone('Europe/London').startOf('day');
  return [d.minus({ days: 7 }).toISODate()!, d.plus({ days: 8 }).toISODate()!];
}

/** Splits a driver's runs: today (in progress first, then by start), later, and earlier ones (newest first). Cancelled runs are dropped. */
export function groupMyRuns<T extends RunListItem>(runs: T[], now: number = Date.now()): { today: T[]; later: T[]; earlier: T[] } {
  const day = londonDay(now);
  const live = runs.filter(r => r.status !== 'cancelled');
  const byStart = (a: T, b: T) => a.planned_start.localeCompare(b.planned_start);
  const today = live.filter(r => londonDay(r.planned_start) === day || r.status === 'in_progress')
    .sort((a, b) => Number(b.status === 'in_progress') - Number(a.status === 'in_progress') || byStart(a, b));
  const rest = live.filter(r => !today.includes(r));
  return {
    today,
    later: rest.filter(r => londonDay(r.planned_start) > day).sort(byStart),
    earlier: rest.filter(r => londonDay(r.planned_start) < day).sort((a, b) => byStart(b, a)),
  };
}

/** The run the Runs tab opens on: the one in progress, else today's next not-finished run, else null. */
export function runToOpen<T extends RunListItem>(runs: T[], now: number = Date.now()): T | null {
  const { today } = groupMyRuns(runs, now);
  return today.find(r => r.status === 'in_progress') ?? today.find(r => r.status !== 'completed') ?? null;
}

// ------------------------------------------------------------------------------------------------ stops
export const isStopDone = (s: Pick<JourneyStop, 'status'>) => s.status === 'completed' || s.status === 'failed';

/** The stop the driver is at or heading to: the first arrived stop, else the first pending one (by seq); null when all are done. */
export function currentStop<S extends Pick<JourneyStop, 'seq' | 'status'>>(stops: S[]): S | null {
  const ordered = [...stops].sort((a, b) => a.seq - b.seq);
  return ordered.find(s => s.status === 'arrived') ?? ordered.find(s => s.status === 'pending') ?? null;
}

export function runProgress(stops: Pick<JourneyStop, 'status'>[]): { done: number; failed: number; total: number; label: string } {
  const done = stops.filter(isStopDone).length;
  const failed = stops.filter(s => s.status === 'failed').length;
  return { done, failed, total: stops.length, label: `${done} of ${stops.length} stop${stops.length === 1 ? '' : 's'} done${failed ? ` · ${failed} failed` : ''}` };
}

export const STOP_STATUS_LABEL: Record<JourneyStop['status'], string> = { pending: 'To do', arrived: 'Arrived', completed: 'Delivered', failed: 'Failed' };

/** "09:00–11:00" in UK time, or null with no window. */
export function stopWindow(s: Pick<JourneyStop, 'window_start' | 'window_end'>): string | null {
  const t = (iso: string) => formatInstantGB(iso, 'HH:mm');
  if (s.window_start && s.window_end) return `${t(s.window_start)}–${t(s.window_end)}`;
  if (s.window_start) return `From ${t(s.window_start)}`;
  if (s.window_end) return `By ${t(s.window_end)}`;
  return null;
}

// ------------------------------------------------------------------------------------------------ navigate
export type MapsApp = 'apple' | 'google';
/** Apple Maps on iPhone, iPad and Mac (it opens the Maps app); Google Maps everywhere else (Android opens the app). */
export function mapsAppFor(userAgent: string): MapsApp {
  return /iPhone|iPad|iPod|Macintosh/.test(userAgent) ? 'apple' : 'google';
}

/** Turn-by-turn link to a stop: coordinates when known, else the address; null when the stop has neither. */
export function navigateUrl(s: Pick<JourneyStop, 'lat' | 'lng' | 'address' | 'label'>, app: MapsApp): string | null {
  const dest = s.lat != null && s.lng != null ? `${s.lat},${s.lng}` : s.address?.trim() ? s.address.trim() : null;
  if (!dest) return null;
  return app === 'apple'
    ? `https://maps.apple.com/?daddr=${encodeURIComponent(dest)}&dirflg=d`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;
}

// ------------------------------------------------------------------------------------------------ proof of delivery
export const FAILED_REASONS = ['Closed', 'No one to sign', 'Refused', 'No access', 'Wrong address', 'Damaged', 'Other'] as const;
export type FailedReason = (typeof FAILED_REASONS)[number];

export interface PodDraft {
  failed: boolean;
  reason?: FailedReason | null;
  name: string;
  note: string;
  photos: number;
  signed: boolean;
}

/** What still stops the driver from sending; mirrors the server's checks (journeys.service complete). Null when it can go. */
export function podBlocker(d: PodDraft): string | null {
  if (d.failed) {
    if (!d.reason) return 'Pick a reason';
    if (d.reason === 'Other' && !d.note.trim()) return 'Say why the delivery failed';
    return null;
  }
  if (!d.signed && d.photos === 0) return 'Add a signature or a photo';
  if (d.signed && !d.name.trim()) return 'Add the name of the person signing';
  return null;
}

/** The pod_note the server stores: the reason first, then the driver's words ("Closed · Shutters down, rang twice"). */
export function podNote(d: Pick<PodDraft, 'failed' | 'reason' | 'note'>): string | null {
  const note = d.note.trim().replace(/\s+/g, ' ');
  if (!d.failed) return note || null;
  if (!d.reason || d.reason === 'Other') return note || null;
  return note ? `${d.reason} · ${note}` : d.reason;
}

// ------------------------------------------------------------------------------------------------ offline overlay
/** Run actions, plus (Phase 3 chunk 2) a vehicle check or defect report saved with no signal; those don't change a run's view. */
export type RunOpKind = 'run.start' | 'run.arrive' | 'run.complete' | 'fleet.check' | 'fleet.report';
/** The part of a queued run op the screen needs to show it (the data layer's RunOutboxOp carries more). */
export interface PendingRunOp {
  id: string;
  kind: RunOpKind;
  journeyId: string;
  stopId?: string | null;
  createdAt: string;
  failed?: boolean;
  pod_name?: string | null;
  pod_note?: string | null;
  pod_pallets?: number | null;
  photos?: number;
  signed?: boolean;
  tracking_consent?: boolean;
}

/**
 * The run as the driver should see it with ops still waiting to send: started, arrived and completed stops applied
 * in order (as the server would), plus which stops are waiting. Ops for other runs are ignored.
 */
export function applyPendingRunOps(view: RunView, ops: PendingRunOp[]): { view: RunView; waitingStopIds: Set<string>; startWaiting: boolean } {
  const mine = ops.filter(o => o.journeyId === view.journey.id && o.kind.startsWith('run.')).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (!mine.length) return { view, waitingStopIds: new Set(), startWaiting: false };
  let journey = { ...view.journey };
  const stops = view.stops.map(s => ({ ...s }));
  const waitingStopIds = new Set<string>();
  let startWaiting = false;
  for (const o of mine) {
    if (o.kind === 'run.start') {
      if (journey.status === 'planned' || journey.status === 'assigned') { journey = { ...journey, status: 'in_progress', tracking_consent: Boolean(o.tracking_consent) }; startWaiting = true; }
      continue;
    }
    const s = stops.find(x => x.id === o.stopId);
    if (!s || isStopDone(s)) continue;
    waitingStopIds.add(s.id);
    if (o.kind === 'run.arrive') { if (s.status === 'pending') { s.status = 'arrived'; s.arrived_at = o.createdAt; } continue; }
    s.status = o.failed ? 'failed' : 'completed';
    s.arrived_at = s.arrived_at ?? o.createdAt;
    s.completed_at = o.createdAt;
    s.pod_name = o.pod_name ?? null; s.pod_note = o.pod_note ?? null; s.pod_pallets = o.pod_pallets ?? null;
    s.pod_photo_count = o.photos ?? 0; s.has_signature = Boolean(o.signed);
  }
  if (journey.status === 'in_progress' && stops.length && stops.every(isStopDone)) journey = { ...journey, status: 'completed' };
  return { view: { ...view, journey, stops }, waitingStopIds, startWaiting };
}

// ------------------------------------------------------------------------------------------------ outbox outcomes
/** docs/offline.md §3.4: what a response means for a queued op. `status` null = no response (offline, timeout, CORS). */
export type OutboxOutcome = 'sent' | 'retry' | 'failed' | 'conflict';
export function classifyOutcome(status: number | null): OutboxOutcome {
  if (status == null) return 'retry';
  if (status >= 200 && status < 300) return 'sent';
  if (status === 408 || status === 429 || status >= 500) return 'retry';
  if (status === 409) return 'conflict';
  return 'failed';
}

/**
 * A 409 on a run action whose effect is already there (someone else, or an earlier send whose answer was lost, did
 * it): the op is done, the server's state wins. Any other 409 (e.g. "Start the run first", an in-flight duplicate)
 * is not.
 */
export const isAlreadyDoneConflict = (message: string | null | undefined) => /already (done|started|completed)|stop already/i.test(message ?? '');
export const isInFlightConflict = (message: string | null | undefined) => /idempotency_in_progress/.test(message ?? '');

/** Backoff for retryable failures: 2 s, 4 s, 8 s … capped at 5 minutes, with ±20% jitter (rand in [0,1)). */
export function outboxRetryDelayMs(attempts: number, rand: number = Math.random()): number {
  const base = Math.min(300_000, 2000 * 2 ** Math.max(0, attempts - 1));
  return Math.round(base * (0.8 + 0.4 * rand));
}

/** The banner line: "Offline · 3 waiting", "Sending 2…", "1 not sent" (failed ones need the driver), or null. */
export function outboxBanner(o: { online: boolean; waiting: number; failed: number; sending?: boolean }): string | null {
  if (o.failed) return `${o.failed} not sent · tap to review`;
  if (!o.waiting) return o.online ? null : 'Offline · changes are saved on this phone';
  if (!o.online) return `Offline · ${o.waiting} waiting`;
  return `Sending ${o.waiting}…`;
}
